<script setup>
import { computed, onMounted, ref } from 'vue';
import { required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useManager } from 'dashboard/composables/useManager';
import validations, { getLabelTitleErrorMessage } from './validations';
import { getRandomColor } from 'dashboard/helper/labelColor';

import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  prefillTitle: {
    type: String,
    default: '',
  },
});
const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();
const { isManager } = useManager();
const uiFlags = useMapGetter('labels/getUIFlags');
const allTeams = useMapGetter('teams/getTeams');
const myTeams = useMapGetter('teams/getMyTeams');

const color = ref('#000');
const description = ref('');
const title = ref('');
const showOnSidebar = ref(true);
const teamId = ref('');

const rules = computed(() => ({
  ...validations,
  teamId: isManager.value ? { required } : {},
}));
const v$ = useVuelidate(rules, {
  color,
  description,
  title,
  showOnSidebar,
  teamId,
});

const teams = computed(() =>
  isManager.value ? myTeams.value : allTeams.value
);
const labelTitleErrorMessage = computed(() =>
  t(getLabelTitleErrorMessage(v$.value))
);
const teamErrorMessage = computed(() =>
  v$.value.teamId.$error ? t('LABEL_MGMT.FORM.TEAM.REQUIRED_ERROR') : ''
);

onMounted(() => {
  color.value = getRandomColor();
  title.value = props.prefillTitle.toLowerCase();
});

const onClose = () => emit('close');

const addLabel = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    await store.dispatch('labels/create', {
      color: color.value,
      description: description.value,
      title: title.value.toLowerCase(),
      show_on_sidebar: showOnSidebar.value,
      team_id: teamId.value || null,
    });
    useAlert(t('LABEL_MGMT.ADD.API.SUCCESS_MESSAGE'));
    onClose();
  } catch (error) {
    const errorMessage = error.message || t('LABEL_MGMT.ADD.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('LABEL_MGMT.ADD.TITLE')"
      :header-content="$t('LABEL_MGMT.ADD.DESC')"
    />
    <form class="flex flex-wrap mx-0" @submit.prevent="addLabel">
      <woot-input
        v-model="title"
        :class="{ error: v$.title.$error }"
        class="w-full [&_input]:lowercase"
        :label="$t('LABEL_MGMT.FORM.NAME.LABEL')"
        :placeholder="$t('LABEL_MGMT.FORM.NAME.PLACEHOLDER')"
        :error="labelTitleErrorMessage"
        data-testid="label-title"
        @input="v$.title.$touch"
        @blur="v$.title.$touch"
      />

      <woot-input
        v-model="description"
        :class="{ error: v$.description.$error }"
        class="w-full"
        :label="$t('LABEL_MGMT.FORM.DESCRIPTION.LABEL')"
        :placeholder="$t('LABEL_MGMT.FORM.DESCRIPTION.PLACEHOLDER')"
        data-testid="label-description"
        @input="v$.description.$touch"
        @blur="v$.description.$touch"
      />

      <div class="w-full">
        <label>
          {{ $t('LABEL_MGMT.FORM.COLOR.LABEL') }}
          <woot-color-picker v-model="color" />
        </label>
      </div>
      <div class="w-full py-2">
        <label
          for="label-team-add"
          class="mb-1 block text-sm font-medium text-n-slate-12"
        >
          {{ $t('LABEL_MGMT.FORM.TEAM.LABEL') }}
        </label>
        <select
          id="label-team-add"
          v-model="teamId"
          class="h-10 w-full rounded-lg border bg-n-alpha-white px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          :class="v$.teamId.$error ? 'border-n-ruby-8' : 'border-n-weak'"
          :required="isManager"
          :aria-invalid="v$.teamId.$error"
          data-testid="label-team"
          @blur="v$.teamId.$touch"
        >
          <option v-if="isManager" disabled value="">
            {{ $t('LABEL_MGMT.FORM.TEAM.SELECT') }}
          </option>
          <option v-else value="">
            {{ $t('LABEL_MGMT.FORM.TEAM.GLOBAL') }}
          </option>
          <option v-for="team in teams" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
        <p v-if="teamErrorMessage" class="mb-0 mt-1 text-xs text-n-ruby-11">
          {{ teamErrorMessage }}
        </p>
      </div>
      <div class="flex items-center w-full gap-2">
        <input
          id="label-show-sidebar-add"
          v-model="showOnSidebar"
          type="checkbox"
          :value="true"
        />
        <label for="label-show-sidebar-add">
          {{ $t('LABEL_MGMT.FORM.SHOW_ON_SIDEBAR.LABEL') }}
        </label>
      </div>
      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('LABEL_MGMT.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          data-testid="label-submit"
          :label="$t('LABEL_MGMT.FORM.CREATE')"
          :disabled="
            v$.title.$invalid || v$.teamId.$invalid || uiFlags.isCreating
          "
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>
